/**
 * Shared Emotion cache + constructable stylesheet for Shadow DOM injection
 * =========================================================================
 *
 * The extension injects ~20 shadow-root hosts into WhatsApp Web. Previously
 * `ShadowDOMThemeProvider` called `createCache({ container: shadowRoot })` PER
 * root, so each host got its own private copy of the full MUI/Emotion CSSOM
 * (hundreds of KB each) — ~20× duplication of identical rules, plus per-mount
 * serialization cost during the startup injection storm.
 *
 * This module builds ONE Emotion cache and ONE constructable CSSStyleSheet,
 * created lazily at first use and shared by every root:
 *
 *  - Because the single cache's `inserted`/`registered` maps are shared, a given
 *    Emotion class-hash is stylis-serialized and inserted EXACTLY ONCE across all
 *    roots (dedupe by hash within one cache = the win). Two roots racing to use
 *    the same class both see it in `cache.inserted` and skip re-insertion.
 *  - The one shared `CSSStyleSheet` is adopted (via `adoptedStyleSheets`) into
 *    every shadow root, so the CSSOM is stored once and referenced N times.
 *
 * Emotion has no built-in constructable-sheet mode, so we install a small
 * adapter: we keep the real `@emotion/sheet` StyleSheet instance Emotion created
 * (so `<Global>`'s `new cache.sheet.constructor(...)` path still works) but swap
 * its `insert`/`flush`/`hydrate` methods so every generated rule is routed into
 * the shared constructable sheet via `insertRule` instead of a per-root <style>.
 *
 * Mirrors the pattern already proven for Tailwind in `tailwindStyles.ts`
 * (getTailwindStyleSheet → one cached CSSStyleSheet → adoptedStyleSheets).
 */
import createCache, { EmotionCache } from '@emotion/cache';

const EMOTION_KEY = 'mui-shadow';

let sharedSheet: CSSStyleSheet | null = null;
let sharedCache: EmotionCache | null = null;
let unsupported = false;

/**
 * Constructable stylesheets + adoptedStyleSheets are required for the shared
 * path. If unsupported, callers fall back to the legacy per-root cache.
 */
export const supportsSharedEmotionSheet = (): boolean => {
  try {
    return (
      typeof CSSStyleSheet !== 'undefined' &&
      'replaceSync' in CSSStyleSheet.prototype &&
      typeof Document !== 'undefined' &&
      'adoptedStyleSheets' in Document.prototype
    );
  } catch {
    return false;
  }
};

/** The single shared constructable sheet (created once), or null if unsupported. */
export const getSharedEmotionSheet = (): CSSStyleSheet | null => {
  if (!supportsSharedEmotionSheet()) return null;
  if (!sharedSheet) {
    sharedSheet = new CSSStyleSheet();
  }
  return sharedSheet;
};

// Dev builds may embed a trailing source-map comment in a serialized rule which
// insertRule can reject; strip it as a fallback before retrying.
const stripSourceMap = (rule: string): string =>
  rule.replace(/\/\*#\s*sourceMappingURL=[^*]+\*\//g, '');

/**
 * The single shared Emotion cache (created once), or null if unsupported.
 * Pass this same object to every `<CacheProvider>` — sharing the cache is what
 * dedupes the CSSOM across roots.
 */
export const getSharedEmotionCache = (): EmotionCache | null => {
  if (unsupported) return null;
  if (sharedCache) return sharedCache;

  const sheet = getSharedEmotionSheet();
  if (!sheet) {
    unsupported = true;
    return null;
  }

  // `prepend: true` mirrors the previous per-root cache; container is unused
  // because our adapter never targets a DOM node (defaults to document.head,
  // which the overridden insert never touches).
  const cache = createCache({ key: EMOTION_KEY, prepend: true });

  // @emotion/utils `insertStyles` calls `cache.insert(sel, serialized,
  // cache.sheet, true)`, and the cache's stylis finalizer calls
  // `cache.sheet.insert(rule)` for each generated CSS rule. We override that
  // instance's methods (keeping it a real StyleSheet so `constructor`,
  // `container`, `tags`, etc. stay valid for the <Global> code path).
  const emotionSheet = cache.sheet as unknown as {
    insert: (rule: string) => void;
    flush: () => void;
    hydrate: (nodes: Node[]) => void;
  };

  emotionSheet.insert = (rule: string) => {
    try {
      sheet.insertRule(rule, sheet.cssRules.length);
    } catch {
      try {
        sheet.insertRule(stripSourceMap(rule), sheet.cssRules.length);
      } catch {
        // Unsupported vendor pseudo etc. — swallow, exactly as @emotion/sheet's
        // own insert() does on insertRule failure.
      }
    }
  };
  // The shared sheet is adopted by many live roots and must persist for the
  // page's lifetime — flushing (Emotion does this on <Global> unmount / SSR)
  // would nuke every root's styles, so it is a no-op here.
  emotionSheet.flush = () => {
    /* shared sheet is never torn down */
  };
  // Nothing to hydrate: a constructable sheet has no SSR <style> nodes.
  emotionSheet.hydrate = () => {
    /* no-op */
  };

  sharedCache = cache;
  return cache;
};

/**
 * Adopt the shared Emotion sheet into a shadow root (idempotent).
 * Returns a teardown that removes it from THIS root only — the shared sheet
 * object and all other roots are unaffected.
 */
export const adoptSharedEmotionSheet = (shadowRoot: ShadowRoot): (() => void) => {
  const sheet = getSharedEmotionSheet();
  if (!sheet || !('adoptedStyleSheets' in shadowRoot)) {
    return () => {
      /* nothing adopted */
    };
  }

  const current = shadowRoot.adoptedStyleSheets ?? [];
  if (!current.includes(sheet)) {
    // Prepend so, among adopted sheets, Emotion stays earliest — matching the
    // previous `prepend: true` ordering (Tailwind, injected as <link> tags, is
    // in the DOM sheet list and still cascades before the adopted sheets).
    shadowRoot.adoptedStyleSheets = [sheet, ...current];
  }

  return () => {
    try {
      const next = (shadowRoot.adoptedStyleSheets ?? []).filter((s) => s !== sheet);
      shadowRoot.adoptedStyleSheets = next;
    } catch {
      /* root already detached */
    }
  };
};
